import { useEffect, useRef } from 'react';

/**
 * Hook to defer pixel/analytics script loading until after React hydration
 * using requestIdleCallback for optimal performance.
 *
 * This ensures pixels load outside the critical render path, improving
 * Time to Interactive (TTI) and Core Web Vitals scores.
 *
 * @param loadFn - Function that loads the pixel script, can optionally return cleanup function
 * @param deps - Dependencies array for the effect
 *
 * @example
 * usePixelLoader(() => {
 *   const script = document.createElement('script');
 *   script.src = 'https://analytics.example.com/pixel.js';
 *   document.head.appendChild(script);
 *
 *   // Return cleanup function to remove script on unmount
 *   return () => {
 *     if (script.parentNode) {
 *       script.parentNode.removeChild(script);
 *     }
 *   };
 * }, [pixelId]);
 */
export const usePixelLoader = (loadFn: () => void | (() => void), deps: React.DependencyList) => {
  const cleanupRef = useRef<(() => void) | void>();
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Track if the effect has been cleaned up to prevent stale callbacks
    let isMounted = true;

    const executeLoad = () => {
      if (!isMounted) return;
      try {
        cleanupRef.current = loadFn();
      } catch (error) {
        // Swallow pixel loading errors to prevent app crashes
        console.error('Error loading pixel:', error);
      }
    };

    // Defer pixel loading until browser is idle
    // This moves the load outside the critical render path
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      // Use requestIdleCallback with a 2-second timeout to ensure pixels load
      // even on busy pages (timeout guarantees execution)
      idleCallbackIdRef.current = window.requestIdleCallback(executeLoad, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback (Safari)
      timeoutIdRef.current = setTimeout(executeLoad, 100);
    }

    // Cleanup on unmount: cancel pending callbacks and run cleanup function
    return () => {
      isMounted = false;

      // Cancel any pending idle callback
      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }

      // Cancel any pending timeout
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      // Run the cleanup function if it was set
      if (cleanupRef.current) {
        try {
          cleanupRef.current();
        } catch (error) {
          console.error('Error cleaning up pixel:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
